import { type FromSchema } from 'json-schema-to-ts';

import { type ClientType, ClientTypeEnum, VatEnum, type VatOptionsKeys } from './enums';

enum InvoiceTemplatesEnum {
  standard = 'standard',
  bold = 'bold',
  modern = 'modern',
  other = 'other',
}

export type Template = keyof typeof InvoiceTemplatesEnum;

export type InvoiceFormData = {
  generatedInvoicePdfRef?: string;
} & InvoiceFormHandlerRequestBody;

// net Type made ourselves since json-to-ts cannot work with previous schema
export type InvoiceData = {
  deliveryDate: Date;
  dueInDays: number | string;
  invoiceDate: Date;
  invoiceNumber: number;
  reference: string;
  isCreditNota: boolean;
};

const invoiceDataSchema = {
  type: 'object',
  properties: {
    invoiceNumber: { type: 'number' },
    invoiceDate: {
      type: 'string',
      format: 'date-time',
    },
    deliveryDate: {
      type: 'string',
      format: 'date-time',
    },
    dueInDays: {
      type: ['number', 'string'],
      minimum: 0,
    },
    reference: {
      type: 'string',
    },
    isCreditNota: {
      type: 'boolean',
    },
  },
  required: ['deliveryDate', 'dueInDays', 'invoiceDate', 'invoiceNumber', 'isCreditNota'],
  additionalProperties: false,
} as const;

// Adress schema and type
export const AddressSchema = {
  type: 'object',
  properties: {
    streetAndNumber: { type: 'string' },
    city: { type: 'string' },
    zipCode: { type: 'string' },
    country: { type: 'string' },
  },
  required: ['streetAndNumber', 'city', 'zipCode', 'country'],
  additionalProperties: false,
} as const;

// Client schema and type
const clientDataSchema = {
  type: 'object',
  properties: {
    type: {
      type: 'string',
      enum: Object.keys(ClientTypeEnum) as readonly ClientType[],
    },
    name: { type: 'string' },
    email: { type: 'string' },
    address: AddressSchema,
    vatNumber: { type: 'string' },
    extraEUIdentificationNumber: { type: 'string' },
    clientRef: { type: 'string' },
  },
  required: ['name', 'address', 'clientRef'],
  additionalProperties: false,
} as const;

export type ClientData = FromSchema<typeof clientDataSchema>;

// User schema and type
const invoiceCompanyDataSchema = {
  type: 'object',
  properties: {
    name: { type: 'string' },
    address: AddressSchema,
    vatNumber: { type: 'string' },
    iban: { type: 'string' },
    bic: { type: 'string' },
    logo: { type: 'string' },
  },
  required: ['name', 'address', 'vatNumber', 'iban', 'bic'],
  additionalProperties: false,
} as const;

export type InvoiceCompanyData = FromSchema<typeof invoiceCompanyDataSchema>;

enum InvoiceStatusLabelEnum {
  dropped = 'dropped',
  deferred = 'deferred',
  bounce = 'bounce',
  delivered = 'delivered',
  open = 'open',
  clicked = 'clicked',
  manualSent = 'manualSent',
}

export type InvoiceStatusLabel = keyof typeof InvoiceStatusLabelEnum;

const invoiceStatusSchema = {
  type: 'object',
  properties: {
    isConcept: { type: 'boolean' },
    isSentToAccountant: { type: 'boolean' },
    isArchived: { type: 'boolean' },
    isSentToClient: { type: 'boolean' },
    isInvoicePaid: { type: 'boolean' },
    isDeleted: { type: 'boolean' },
    clientMailStatus: {
      type: 'string',
      enum: Object.keys(InvoiceStatusLabelEnum) as readonly InvoiceStatusLabel[],
    },
  },
  additionalProperties: false,
} as const;

export type InvoiceStatus = FromSchema<typeof invoiceStatusSchema>;

// export enum VatEnum {
//   noBtw = "noBtw",
//   six = "six",
//   twelve = "twelve",
//   twentyOne = "twentyOne",
//   ICD = "ICD",
//   ICL = "ICL",
//   EX = "EX",
//   KO = "KO",
// }

// export type VatOptionsKeys = keyof typeof VatEnum;

// item schema and type
const itemSchema = {
  type: 'object',
  properties: {
    description: { type: 'string' },
    pricePerUnit: { type: 'number' },
    quantity: { type: 'number' },
    discount: { type: 'number' },
    discountType: { type: 'string' },
    vat: { type: 'number' },
    vatPercentage: { type: 'number' },
    vatType: {
      type: 'string',
      enum: Object.keys(VatEnum) as readonly VatOptionsKeys[],
    },
    subtotal: { type: 'number' },
    total: { type: 'number' },
  },
  required: [
    'description',
    'pricePerUnit',
    'quantity',
    'vat',
    'vatType',
    'vatPercentage',
    'subtotal',
    'total',
  ],
  additionalProperties: false,
} as const;

export type Item = FromSchema<typeof itemSchema>;

// items schema and type
const itemsSchema = {
  type: 'array',
  items: itemSchema,
} as const;

export type Items = FromSchema<typeof itemsSchema>;

// vatdata per vat type schema and type
const vatDataPerVatTypeSchema = {
  type: 'object',
  properties: {
    total: { type: 'number' },
    percentage: { type: 'number' },
    type: { type: 'string' },
  },
  required: ['total', 'percentage'],
  additionalProperties: false,
} as const;

export type VatDataPerVatType = FromSchema<typeof vatDataPerVatTypeSchema>;

// vatData schema and type
const vatDataSchema = {
  type: 'array',
  items: vatDataPerVatTypeSchema,
} as const;

export type VatData = FromSchema<typeof vatDataSchema>;

export const invoiceFormHandlerRequestBodySchema = {
  type: 'object',
  properties: {
    invoiceData: invoiceDataSchema,
    clientData: clientDataSchema,
    invoiceCompanyData: invoiceCompanyDataSchema,
    items: itemsSchema,
    vatData: vatDataSchema,
    subtotal: { type: 'number' },
    total: { type: 'number' },
    notes: { type: 'string' },
    status: invoiceStatusSchema,
    settings: {
      type: 'object',
      properties: {
        templateId: {
          type: 'string',
          enum: ['standard', 'other', 'bold', 'modern'],
        },
        defaultHexColor: { type: 'string' },
        hasTermsAndConditions: { type: 'boolean' },
        termsAndConditions: { type: 'string' },
        emailBody: { type: 'string' },
      },
      required: ['templateId', 'defaultHexColor'],
      additionalProperties: false,
    },
  },
  required: [
    'invoiceData',
    'clientData',
    'invoiceCompanyData',
    'items',
    'settings',
    'subtotal',
    'total',
  ],
  additionalProperties: false,
} as const;

export type MakeInvoiceSettingsType = {
  templateId?: Template;
  defaultHexColor?: string;
  hasTermsAndConditions?: boolean;
  termsAndConditions?: string;
  emailBody?: string;
};

export type InvoiceFormHandlerRequestBody = {
  invoiceData: InvoiceData;
  clientData: ClientData;
  invoiceCompanyData: InvoiceCompanyData;
  items: Items;
  vatData: VatData;
  settings: MakeInvoiceSettingsType;
  subtotal: number;
  total: number;
  notes?: string;
  status?: InvoiceStatus;
  isCreditNota?: boolean;
};

export type VatTableData = {
  vatType: string;
  subtotal: number;
  vat: number;
}[];

export type ExemptionData = {
  line: string;
}[];

export const emailToClientRequestBodySchema = {
  type: 'object',
  properties: {
    userId: { type: 'string' },
    invoiceId: { type: 'string' },
    hasUserAddBcc: { type: 'boolean' },
    bcc: { type: 'array', items: { type: 'string' } },
    receiver: { type: 'string' },
    subject: { type: 'string' },
    body: { type: 'string' },
  },
  required: ['invoiceId', 'receiver', 'subject', 'body', 'userId'],
  additionalProperties: false,
} as const;

export type EmailToClientRequestBody = FromSchema<typeof emailToClientRequestBodySchema>;
